/**
 * Call State Configuration Accessors
 *
 * Pure functions for accessing CallState configuration, flags, permissions, and UI properties.
 */

import { CALL_STATE_CONFIG } from 'Modules/softphone/constants';
import type {
  CallState,
  CallStateFlags,
  CallStateConfig,
  CallPermissions,
} from 'Modules/softphone/types';

/**
 * Gets the complete configuration for a CallState
 *
 * @param state - The CallState to get config for
 * @returns The complete state configuration
 */
export function getCallStateConfig(state: CallState): CallStateConfig {
  return CALL_STATE_CONFIG[state];
}

/**
 * Gets the flags for a CallState
 *
 * @param state - The CallState to get flags for
 * @returns The state flags
 */
export function getCallStateFlags(state: CallState): CallStateFlags {
  return CALL_STATE_CONFIG[state].flags;
}

/**
 * Gets the permissions for a CallState
 * Determines what actions are allowed in each state
 *
 * @param state - The CallState to get permissions for
 * @returns The permissions object
 */
export function getCallPermissions(state: CallState): CallPermissions {
  return CALL_STATE_CONFIG[state].permissions;
}

/**
 * Gets the display label for a CallState
 *
 * @param state - The CallState to get label for
 * @returns The display label
 */
export function getCallStateLabel(state: CallState): string {
  return CALL_STATE_CONFIG[state].label;
}

/**
 * Gets the color class for a CallState
 *
 * @param state - The CallState to get color for
 * @returns The Tailwind color class
 */
export function getCallStateColor(state: CallState): string {
  return CALL_STATE_CONFIG[state].color;
}
